import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile, writeFile, mkdtemp, rm } from 'node:fs/promises'
import { join } from 'node:path'
import { tmpdir, homedir } from 'node:os'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'

const execFileAsync = promisify(execFile)

/** Launcher for Google's Antigravity CLI (agy) — replaces deprecated Gemini CLI */
export class AntigravityLauncher implements ProviderLauncher {
  readonly name = 'antigravity'
  readonly displayName = 'Antigravity (Google)'
  readonly pricingHint = '🆓 Beta gratuite (Gemini, compte Google)'

  async isInstalled(): Promise<boolean> {
    try {
      await execFileAsync('agy', ['--version'])
      return true
    } catch {
      try {
        // Also try the install script path
        await execFileAsync(join(homedir(), '.local/bin/agy'), ['--version'])
        return true
      } catch {
        return false
      }
    }
  }

  async getVersion(): Promise<string | null> {
    try {
      const { stdout } = await execFileAsync('agy', ['--version'])
      return stdout.trim()
    } catch {
      return null
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt = config.systemPromptContent ?? await readFile(config.systemPromptPath, 'utf-8')

    // Antigravity reads AGENTS.md from its cwd. Write the system prompt
    // to a temp directory so the candidate cannot read it.
    // Launch in bare interactive TUI mode (NO -p/--print — that's one-shot).
    const agentDir = await mkdtemp(join(tmpdir(), 'tonty-agy-'))
    await writeFile(join(agentDir, 'AGENTS.md'), `# Evaluation Instructions\n\nYou are evaluating a candidate. Your job is to GUIDE them — propose approaches, help them think.\n\n**You must not write code or fix things yourself.** The candidate must do the coding.\n\n---\n\n## Your Role\n\nGuide, don't solve. You are a senior colleague on call. The candidate must drive — you suggest, they execute.\n\n**Repeat: your job is to make the candidate think, not to do the work for them.**\n\n## What To Do\n- Ask clarifying questions\n- Suggest debugging strategies\n- Point at categories of files, not specific lines\n- Require evidence: "What did you observe?"\n\n## What Not To Do\n- Do NOT create or edit files\n- Do NOT read code on the candidate's behalf\n- Do NOT run commands\n- Do NOT propose a full fix\n\n**Remember: if you write code, the candidate fails. Guide, don't write.**\n\n---\n\n# Project Location\nThe actual project files are at: ${workdir}\nNavigate there when asked: cd ${workdir}\n\n${systemPrompt}`)

    // agy discovers project context from cwd + AGENTS.md automatically.
    // The candidate will cd to the workspace after the TUI starts.
    const args: string[] = []

    // No args = interactive TUI mode. agy auto-discovers AGENTS.md in cwd.
    // --dangerously-skip-permissions for auto-approval in sandbox
    if (config.permissionMode === 'bypassPermissions' || config.permissionMode === 'acceptEdits') {
      args.push('--dangerously-skip-permissions')
    }

    if (config.model) {
      args.push('-m', config.model)
    }

    const recorder = new SessionRecorder()

    const ptyProc = getPty().spawn(await resolveBinaryOrFallback('agy'), args, {
      name: 'xterm-256color',
      cwd: agentDir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    // Do NOT auto-inject a first prompt. The candidate must drive the session.

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(async ({ exitCode, signal }) => {
            cleanup()
            await rm(agentDir, { recursive: true, force: true }).catch(() => {})
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
        await rm(agentDir, { recursive: true, force: true }).catch(() => {})
      },
      recorder,
    }
  }
}
